import * as React from "react";
import Axios, { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { Grid, Paper } from "@mui/material";

import Title from "@/components/molecules/Title";
import { ChargerExcludeCreateEdit } from "@/components/chargerExclude/ChargerExcludeCreateEdit";
import toast from "@/components/hoc/SnackbarAlert";
import { ChargerExcludeConstants } from "./ChargerExcludeConstants";
import { useAuthUser } from "@/hooks/useAuthUser";
import {
  ChargerExcludeErrorType,
  ChargerExcludeInputModel,
} from "@/models/chargerExclude";
import { Office, PLUS_CITY, ONLY_CONTRACT } from "@/models/office";
import { ChargerNames } from "@/models/chargerNames";
import { GX_ADMIN } from "@/models/auth";
import { createChargerExcludeAPI } from "@/util/api/chargerExclude";
import { getChargerNamesAPI, getOfficesAPI } from "@/util/api/API";
import { getJSTCurrentTime } from "@/util/datetime";
import { Messages } from "@/util/Messages";
import { CommonConstants, ErrorResponse } from "@/common/CommonConstants";
import { useLoadingPromise } from "@/hooks/useLoadingPromise";
import { ActionButtons } from "@/components/common/buttonGroup/ActionButtons";

export const ChargerExcludeCreate = () => {
  const [inputData, setInputData] = React.useState<ChargerExcludeInputModel>({
    officeId: undefined,
    chargerId: undefined,
    startAt: getJSTCurrentTime("datetime"),
    cause: "",
  });
  const [errorData, setErrorData] = React.useState<ChargerExcludeErrorType>({});
  const [chargerNames, setChargerNames] = React.useState<
    Partial<ChargerNames>[]
  >([]);
  const [chargerNamesSelected, setChargerNamesSelected] =
    React.useState<ChargerNames>();
  const [offices, setOffices] = React.useState<Office[]>([]);
  const [officeSelected, setOfficeSelected] = React.useState<Office>();
  const [inRange, setInRange] = React.useState(true);
  const { user } = useAuthUser();

  const [loading, createChargerExcludeAPIWrapper] = useLoadingPromise(
    createChargerExcludeAPI
  );

  const navigate = useNavigate();

  //Get list office to select
  const getOffices = React.useCallback(async () => {
    try {
      const res = await getOfficesAPI(
        user?.role === GX_ADMIN ? PLUS_CITY : ONLY_CONTRACT
      );
      setOffices(res.data);
    } catch (err) {
      console.log("==Error when get offfices data==", err);
    }
  }, [user?.role]);

  //Get list chargerNames to select
  const getChargerNames = async (officeId: number) => {
    try {
      const res = await getChargerNamesAPI(officeId);
      setChargerNames(res.data);
    } catch (err) {
      console.log("==Error when get chargerNames data==", err);
    }
  };

  // Handle event change data of charger
  const onChangeData = (
    key: keyof typeof inputData,
    value: string | number
  ) => {
    setInputData({ ...inputData, [key]: value });
    setErrorData({ ...errorData, [key]: false });
  };

  const handleChangeDate = React.useCallback(
    (newDate?: string, inRange?: boolean) => {
      setInputData({ ...inputData, startAt: newDate });
      setErrorData({ ...errorData, startAt: false });
      setInRange(inRange ?? true);
    },
    [inputData, errorData]
  );

  // Handle event create charger, check validate and fetch API
  const onSubmit = async () => {
    // Check validate each field
    const errorDataTemp: ChargerExcludeErrorType = {
      startAt: !inputData.startAt || !inRange,
      officeId: !inputData.officeId?.toString().length,
      chargerId: !inputData.chargerId,
      cause: !inputData.cause,
    };

    // If invalid then return and show error
    if (Object.values(errorDataTemp).some((it) => it)) {
      setErrorData({ ...errorDataTemp });
      return;
    } else {
      setErrorData({});
    }

    const { officeId, ...dataSend } = inputData;

    createChargerExcludeAPIWrapper({
      ...dataSend,
      endAt: null,
      version: null,
    })
      .then(() => {
        goToListExcludeCharger();
      })
      .catch((e: AxiosError<ErrorResponse>) => {
        if (
          Axios.isAxiosError(e) &&
          e.response &&
          (e.response.status === 404 || e.response.status === 409)
        ) {
          toast.error(
            CommonConstants.Register +
              Messages.ErrorOccurred +
              "：" +
              e?.response?.data?.message
          );
        }
        console.log("==Error when create charger exclude==", e);
      });
  };

  // Handle redirect to ExcludeCharger list
  const goToListExcludeCharger = () => {
    navigate("/charger_exclude");
  };

  // handle event change chargerNames select
  const onChangeChargerNames = (value: ChargerNames | undefined) => {
    setChargerNamesSelected(value);
    setInputData({ ...inputData, chargerId: value?.id || undefined });
  };

  // Handle event change office select
  const onChangeOffice = (value: Office | undefined) => {
    setInputData({
      ...inputData,
      officeId: value?.id || undefined,
      chargerId: undefined,
    });
    setChargerNames([]);
    setOfficeSelected(value);
    setChargerNamesSelected(undefined);
    if (value?.id !== undefined) {
      getChargerNames(value.id);
    }
  };

  // Load offices
  React.useEffect(() => {
    getOffices();
  }, [getOffices]);

  return (
    <React.Fragment>
      <Grid
        container
        spacing={2}
        sx={{ paddingLeft: 2, paddingRight: 2, mt: 3 }}
      >
        <Grid container spacing={2} sx={{ display: "flex", pl: 2 }}>
          <Title>{ChargerExcludeConstants.ChargerExcludeNewRegistration}</Title>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ paddingLeft: 2, paddingRight: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={10} md={10} lg={14}>
            <Paper sx={{ p: 1, display: "flex", flexDirection: "column" }}>
              <ChargerExcludeCreateEdit
                inputData={inputData}
                chargerNamesSelected={chargerNamesSelected}
                chargerNames={chargerNames as ChargerNames[]}
                errorData={errorData}
                officeSelected={officeSelected}
                offices={offices}
                onChangeData={onChangeData}
                onChangeOffice={onChangeOffice}
                onChangeChargerNames={onChangeChargerNames}
                onChangeDate={handleChangeDate}
              />
              <ActionButtons
                loading={loading}
                forSave
                onCancel={goToListExcludeCharger}
                onSubmit={onSubmit}
              />
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    </React.Fragment>
  );
};
